import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import { visuallyHidden } from "@mui/utils";
import { format } from "date-fns";
import { EOrderStatusDescription } from "enums/options/orders";
import { useNavigate } from "react-router-dom";

interface Data {
  name: any;
  phone: string;
  idOrder: string;
  dealer: string;
  dealerId: string;
  cupom: any;
  compain: any;
  data: any;
  status: string;
}
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number,
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => void;
  order: Order;
  orderBy: string;
  // eslint-disable-next-line react/no-unused-prop-types
  rowCount?: number;
  // eslint-disable-next-line react/no-unused-prop-types
  headCells: any;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, headCells, onRequestSort } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell: any, index: number) => (
          <TableCell
            style={{ background: "#D33100" }}
            key={headCell.id}
            align="left"
            padding="normal"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              style={{
                color: "#FFFFFF",
                marginRight: "5px",
              }}
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {index === 0 ? (
                headCell.label
              ) : (
                <div
                  style={{
                    color: "#FFFFFF",
                    paddingLeft: "7px",
                    borderLeft: "2px solid #FFFFFF",
                  }}
                >
                  {headCell.label}
                </div>
              )}

              {orderBy === headCell.id ? (
                <Box
                  component="span"
                  style={{ color: "#FFFFFF" }}
                  sx={visuallyHidden}
                >
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function TableManagerOrders({ rows, headCells }: any) {
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("name");
  const [page] = React.useState(0);
  const [rowsPerPage] = React.useState(10000);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const navigate = useNavigate();

  const navigateToDealerById = (id: string) => {
    navigate(`/dealers/${id}`);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer style={{ padding: "16px" }}>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <EnhancedTableHead
              headCells={headCells}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {/* if you don't need to support IE11, you can replace the `stableSort` call with:
              rows.slice().sort(getComparator(order, orderBy)) */}
              {stableSort(rows, getComparator(order, orderBy)).map(
                (row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow tabIndex={-1} key={row.name}>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        align="left"
                      >
                        {row.name}
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        align="left"
                      >
                        {row.phone as string}
                      </TableCell>
                      <TableCell align="left">{row.idOrder}</TableCell>
                      <TableCell align="left">
                        <button
                          type="button"
                          style={{
                            padding: 0,
                            border: 0,
                            background: "transparent",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          onClick={() =>
                            navigateToDealerById(row.dealerId as string)
                          }
                        >
                          {row.dealer}
                        </button>
                      </TableCell>
                      <TableCell align="left">{row.cupom}</TableCell>
                      <TableCell align="left">{row.compain}</TableCell>
                      <TableCell align="left">
                        {format(new Date(row.data), "dd/MM/yyyy")}
                      </TableCell>
                      <TableCell align="left">
                        {EOrderStatusDescription[row.status as any]}
                      </TableCell>
                    </TableRow>
                  );
                },
              )}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 40 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
